import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { Logo } from '../components/Logo';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { useI18n } from '../lib/i18n';

export function Login() {
  const nav = useNavigate();
  const { setAuth } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleGoogle(idToken: string) {
    setLoading(true); setErr(null);
    try {
      const r = await api.google(idToken);
      setAuth(r.token, r.user);
      nav('/app');
    } catch (e: any) {
      setErr(e.message ?? 'error');
    } finally { setLoading(false); }
  }

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setErr(null);
    try {
      const r = await api.login({ email, password });
      setAuth(r.token, r.user);
      nav('/app');
    } catch (e: any) {
      setErr(e.message ?? 'error');
    } finally { setLoading(false); }
  }

  return (
    <AuthLayout>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-sm rounded-3xl p-7 text-center"
      >
        <h1 className="h-display text-3xl">{t('auth.login.title')}</h1>
        <p className="mt-1 text-haze text-sm">{t('auth.login.subtitle')}</p>

        <div className="mt-7">
          <GoogleSignInButton onCredential={handleGoogle} />
        </div>

        <div className="my-6 flex items-center gap-3 text-2xs uppercase tracking-widest text-haze">
          <span className="flex-1 divider" />
          <span>{t('auth.login.orEmail')}</span>
          <span className="flex-1 divider" />
        </div>

        <form onSubmit={handleEmailLogin} className="text-left space-y-3">
          <input
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.login.emailPlaceholder')}
            className="input"
          />
          <input
            type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.login.passwordPlaceholder')}
            className="input"
          />
          <button type="submit" disabled={loading} className="btn-ghost w-full">
            {loading ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>
        </form>

        {err && <p className="mt-4 text-sm text-flame">{err}</p>}

        <p className="mt-5 text-sm text-haze">
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className="text-foam font-semibold underline">{t('auth.login.createOne')}</Link>
        </p>
      </motion.div>
    </AuthLayout>
  );
}

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh relative grid place-items-center px-4 py-10">
      <div className="atmosphere atmosphere--mix" />
      <div className="pointer-events-none absolute inset-0 bg-grid -z-10 opacity-40" />
      <div className="relative w-full grid place-items-center">
        <Link to="/" className="mb-6 opacity-90">
          <Logo size={32} />
        </Link>
        {children}
      </div>
    </div>
  );
}
